import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, UserPlus, CheckCircle, Mail, Lock, User, BarChart3, Sparkles } from 'lucide-react';
import GoogleSignInButton from '../../components/GoogleSignInButton';
import { apiFetch } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function Register() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const getPasswordStrength = () => {
    if (!password) return { label: '', color: '', width: '0%' };
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 2) return { label: 'Weak', color: '#ef4444', width: '33%' };
    if (score <= 3) return { label: 'Medium', color: '#f59e0b', width: '66%' };
    return { label: 'Strong', color: '#10b981', width: '100%' };
  };

  const strength = getPasswordStrength();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(API_ROUTES.auth.register, {
        method: 'POST',
        body: { email, username, password },
        auth: false,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Registration failed. Please try again.');
        return;
      }
      setSuccess(true);
    } catch (e) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#070912] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px] animate-float-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-[130px] animate-float" />
        </div>
        <div className="w-full max-w-md relative z-10 animate-scale-in">
          <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-emerald-400/30 via-emerald-400/10 to-transparent">
            <div className="rounded-3xl p-8 text-center backdrop-blur-xl" style={{
              background: 'linear-gradient(135deg, rgba(15,20,40,0.85), rgba(10,13,31,0.9))',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 60px rgba(16,185,129,0.1)',
            }}>
              <div className="w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center animate-pulse-soft" style={{
                background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(20,184,166,0.1))',
                boxShadow: '0 0 40px rgba(16,185,129,0.3)',
              }}>
                <CheckCircle size={40} className="text-emerald-400" />
              </div>
              <h2 className="font-display text-2xl font-bold text-white mb-3">Check your email</h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                We sent a verification link to <span className="text-emerald-400 font-medium">{email}</span>.
                Click the link to activate your account.
              </p>
              <Link to="/login" className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl transition-all" style={{
                background: 'linear-gradient(135deg, #10b981, #14b8a6)',
                color: '#070912',
                boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
              }} onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(16,185,129,0.4)';
              }} onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(16,185,129,0.3)';
              }}>
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070912] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient mesh gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px] animate-float-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-[130px] animate-float" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-purple-500/5 blur-[150px]" />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo */}
        <Link to="/" className="flex flex-col items-center gap-3 mb-8 group">
          <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform">
            <BarChart3 size={26} className="text-white" />
          </div>
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">TrackMyFxEA</h1>
            <p className="text-gray-500 text-xs mt-1">Professional EA Performance Analytics</p>
          </div>
        </Link>

        {/* Card */}
        <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-white/15 via-white/5 to-transparent">
          <div className="rounded-3xl p-8 backdrop-blur-xl" style={{
            background: 'linear-gradient(135deg, rgba(15,20,40,0.85), rgba(10,13,31,0.9))',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          }}>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="font-display text-2xl font-bold text-white">Create your account</h2>
                <Sparkles size={18} className="text-emerald-400" />
              </div>
              <p className="text-sm text-gray-500">Start tracking your EAs for free</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username */}
              <div>
                <label htmlFor="register-username" className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Username</label>
                <div className="relative group">
                  <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-emerald-400 transition-colors" />
                  <input
                    id="register-username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-400/50 focus:bg-black/40 focus:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all backdrop-blur-sm"
                    placeholder="trader_pro"
                    required
                    minLength={3}
                    maxLength={30}
                    pattern="[a-zA-Z0-9_]+"
                    title="Letters, numbers, and underscores only"
                    autoComplete="username"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="register-email" className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Email</label>
                <div className="relative group">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-emerald-400 transition-colors" />
                  <input
                    id="register-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-400/50 focus:bg-black/40 focus:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all backdrop-blur-sm"
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="register-password" className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Password</label>
                <div className="relative group">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-emerald-400 transition-colors" />
                  <input
                    id="register-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-11 pr-12 py-3.5 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-400/50 focus:bg-black/40 focus:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all backdrop-blur-sm"
                    placeholder="Min 8 characters"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {password && (
                  <div className="mt-2.5">
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full transition-all duration-300" style={{ width: strength.width, background: strength.color }} />
                    </div>
                    <p className="text-xs mt-1.5" style={{ color: strength.color }}>{strength.label} password</p>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="register-confirm-password" className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Confirm Password</label>
                <div className="relative group">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 transition-colors" style={{
                    color: confirmPassword && confirmPassword !== password ? '#ef4444' : undefined
                  }} />
                  <input
                    id="register-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-black/30 border rounded-xl pl-11 pr-4 py-3.5 text-gray-200 placeholder-gray-600 focus:outline-none focus:bg-black/40 transition-all backdrop-blur-sm"
                    style={{
                      borderColor: confirmPassword && confirmPassword !== password ? '#ef4444' : 'rgba(255,255,255,0.1)',
                    }}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                </div>
                {confirmPassword && confirmPassword !== password && (
                  <p className="text-xs text-red-400 mt-1.5">Passwords don't match</p>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="rounded-xl px-4 py-3 text-red-400 text-sm animate-fade-in" style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.25)',
                }}>
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full font-bold py-3.5 rounded-xl transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #10b981, #14b8a6, #06b6d4)',
                  color: '#070912',
                  boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(16,185,129,0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(16,185,129,0.3)';
                }}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[#070912] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <UserPlus size={16} />
                    Create Account
                  </>
                )}
              </button>
            </form>

            {/* Google Sign-In */}
            <div className="my-5 relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/8" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 text-gray-600" style={{ background: 'rgb(11,15,32)' }}>or sign up with</span>
              </div>
            </div>
            <GoogleSignInButton text="signup_with" />

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/8" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 text-gray-600" style={{ background: 'rgb(11,15,32)' }}>OR</span>
              </div>
            </div>

            <p className="text-center text-gray-500 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors">
                Sign in →
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center mt-6">
          <Link to="/" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
